import { gatewayFetch, gatewayRoot } from '../data/http';
import { newChallenge, takeVerifier } from './pkce';

/* ВХОД В КАБИНЕТ. Дорога та же, что у публичной страницы врача и у приложения
   врача, и это не совпадение: обмен кода на токены умеет только гейтвей — клиент
   там КОНФИДЕНЦИАЛЬНЫЙ, его секрет в браузер не отдают.

     1. GET {gw}/api/v1/login/link?redirect_url={origin}/login&code_challenge=…&client_type=web
        → { link } — адрес страницы авторизации Keycloak, собранный гейтвеем
        (`code_challenge` — PKCE, см. lib/pkce.ts);
     2. уход на этот адрес. Номер и код пациент вводит ТАМ — поле принадлежит
        телефонному плагину Keycloak (тема `aventrae-patient`);
     3. Keycloak возвращает на {origin}/login?code=… ;
     4. POST {gw}/api/v1/login { code, redirect_uri, code_verifier, client_type } → 204 и
        HttpOnly-куки. Токена кабинет не держит и держать не может.

   `client_type: 'web'` НЕСУЩЕЕ: гейтвей провижинит аккаунт с типом `patient`
   именно для web, и роль выводит из типа аккаунта.

   ⚠️ АДРЕС КАБИНЕТА ДОЛЖЕН БЫТЬ РАЗРЕШЁН У КЛИЕНТА `web_ui` В KEYCLOAK. Он
   сверяет `redirect_uri` ДО отрисовки страницы, поэтому неразрешённый адрес даёт
   отказ Keycloak, а не наш. Это настройка админки, из кода её не обойти. */

/** Куда Keycloak вернёт пациента. Из ТЕКУЩЕГО происхождения, а не из константы:
    адрес должен совпасть с разрешённым у клиента, а стендов несколько. Он же
    уезжает вторым разом в обмен кода — Keycloak сверяет совпадение. */
export function loginRedirectUri(): string {
    return `${window.location.origin}/login`;
}

export type StartResult = { ok: false; message: string };

/** Шаги 1-2: взять у гейтвея адрес авторизации и уйти на него.
    Возвращает управление ТОЛЬКО при отказе: успех — это уход со страницы. */
export async function startLogin(): Promise<StartResult> {
    if (gatewayRoot() === null) return { ok: false, message: 'вход недоступен в этом окружении' };
    let challenge: string;
    try {
        challenge = await newChallenge();
    } catch {
        return { ok: false, message: 'браузер не дал начать вход' };
    }
    try {
        const res = await gatewayFetch(
            `/login/link?redirect_url=${encodeURIComponent(loginRedirectUri())}` +
                `&code_challenge=${challenge}&client_type=web`,
        );
        if (!res.ok) return { ok: false, message: 'не удалось начать вход' };
        const body = await res.json();
        const link = typeof body?.link === 'string' ? body.link : '';
        if (!link) return { ok: false, message: 'гейтвей не дал адрес авторизации' };
        const state = rememberState();
        // Метку некуда положить — вход всё равно не завершится, честнее не начинать.
        if (state === null) return { ok: false, message: 'не удалось начать вход' };
        window.location.href = withState(link, state);
        return { ok: false, message: '' }; // уходим со страницы; сообщение не покажется
    } catch {
        // Сюда же попадает отказ CORS: из браузера он неотличим от обрыва сети.
        return { ok: false, message: 'нет связи, попробуйте ещё раз' };
    }
}

/* `STATE` — «НОМЕР ЗАЯВКИ» НА ВХОД (аудит 25.10), как в приложении врача
   (`aventrae/src/lib/auth.ts`, с 20.10) и на странице врача (`doctor-page-ui`).
   Перед уходом в Keycloak кабинет создаёт случайную строку, дописывает её к
   адресу авторизации и запоминает во вкладке; Keycloak возвращает её нетронутой
   рядом с кодом. Код меняется на куки, только если вернулась та же строка.

   Без этого `/login?code=…` принимал ЛЮБОЙ код: чужой код, подсунутый ссылкой,
   тихо вписывал пациента в чужой аккаунт. Гейтвей и Keycloak не меняются:
   `state` — стандартный параметр OAuth.

   `sessionStorage`, а не `localStorage`: метка нужна ровно этой вкладке и ровно
   этой попытке. Одноразовая и живёт 10 минут. Начал вход в одной вкладке, а
   закончил в другой — вход начинается заново; это и есть цена защиты. */
const LOGIN_STATE = 'aventrae.login.state';
const LOGIN_STATE_TTL_MS = 10 * 60 * 1000;

/** Создать и запомнить метку; null — хранилище вкладки недоступно. 128 бит, hex. */
function rememberState(): string | null {
    try {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        window.sessionStorage.setItem(LOGIN_STATE, JSON.stringify({ state, at: Date.now() }));
        return state;
    } catch {
        return null;
    }
}

/** Забрать метку — один раз (запись стирается); просроченная не считается. */
function takeState(): string | null {
    try {
        const raw = window.sessionStorage.getItem(LOGIN_STATE);
        window.sessionStorage.removeItem(LOGIN_STATE);
        if (!raw) return null;
        const p = JSON.parse(raw) as { state?: unknown; at?: unknown };
        if (typeof p.state !== 'string' || typeof p.at !== 'number') return null;
        return Date.now() - p.at > LOGIN_STATE_TTL_MS ? null : p.state;
    } catch {
        return null;
    }
}

/** Дописать метку к адресу авторизации через `URL`: гейтвей отдаёт адрес с
    незакодированным `redirect_uri`, и склейка строк могла бы задеть его хвост. */
function withState(link: string, state: string): string {
    try {
        const url = new URL(link);
        url.searchParams.set('state', state);
        return url.toString();
    } catch {
        return `${link}${link.includes('?') ? '&' : '?'}state=${encodeURIComponent(state)}`;
    }
}

export type FinishResult = { ok: true } | { ok: false; message: string };

/** Шаг 4: обменять код на куки. `redirect_uri` тот же, что на шаге 1 — Keycloak
    сверяет их совпадение, и любое расхождение это 401 без объяснений. */
export async function finishLogin(code: string, state: string | null): Promise<FinishResult> {
    const verifier = takeVerifier();
    // Вход начат не в этой вкладке (или подсунута чужая ссылка с кодом) — без
    // verifier Keycloak код всё равно не обменяет.
    if (verifier === null)
        return { ok: false, message: 'вход начат в другой вкладке, начните заново' };

    const expected = takeState();
    if (expected === null || state !== expected) {
        return { ok: false, message: 'код входа не подошёл, начните заново' };
    }
  
    try {
        const res = await gatewayFetch('/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                code,
                redirect_uri: loginRedirectUri(),
                code_verifier: verifier,
                client_type: 'web',
            }),
        });
        if (res.status === 401)
            return { ok: false, message: 'код входа не подошёл, начните заново' };
        if (!res.ok) return { ok: false, message: 'не удалось завершить вход' };
        return { ok: true };
    } catch {
        return { ok: false, message: 'нет связи, попробуйте ещё раз' };
    }
}

/** Выйти: гейтвей гасит сессию в Keycloak и стирает куки СВОИМ ОТВЕТОМ.
    Стереть их из JS нельзя — они `HttpOnly`, ровно как при входе. */
export async function logout(): Promise<boolean> {
    if (gatewayRoot() === null) return false;
    try {
        const res = await gatewayFetch('/logout', { method: 'POST' });
        return res.ok;
    } catch {
        return false;
    }
}

/** Что принёс Keycloak на `/login`: код с меткой, отказ или ничего (обычный заход). */
export function callbackFromLocation(): {
    code: string | null;
    state: string | null;
    error: string | null;
} {
    const q = new URLSearchParams(window.location.search);
    return { code: q.get('code'), state: q.get('state'), error: q.get('error') };
}
